

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
// reCAPTCHA site key, injected at build time via CRA env var
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;


// Utility to get API base URL depending on environment
const getApiBaseUrl = () => {
  if (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  ) {
    return 'http://localhost:3000';
  }
  // Always use the production backend for any other hostname
  // Use relative URL for production
  return '';
};

const CreateProjectPage = () => {
  // Loader and popup styles (must be here, not in useEffect or JSX)
  const loaderStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(255,255,255,0.7)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    loader: {
      fontSize: 24,
      color: '#28a745',
      fontWeight: 'bold',
      background: '#fff',
      padding: '24px 40px',
      borderRadius: 10,
      boxShadow: '0 2px 12px 0 rgba(46,204,64,0.08)',
    },
  };

  const popupStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.25)',
      zIndex: 1100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    popup: {
      background: '#fff',
      borderRadius: 10,
      padding: '32px 24px 24px 24px',
      boxShadow: '0 2px 12px 0 rgba(46,204,64,0.12)',
      minWidth: 280,
      maxWidth: 350,
      textAlign: 'center',
    },
    message: {
      marginBottom: 10,
      fontSize: 16,
    },
  };
  const [name, setName] = useState('');
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [tools, setTools] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);
  const [objects, setObjects] = useState([]);
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [difficulty, setDifficulty] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState(''); // 'success' or 'error'
  const [showAddObject, setShowAddObject] = useState(false);
  const [newObjectName, setNewObjectName] = useState('');
  const [showAddTool, setShowAddTool] = useState(false);
  const [newToolName, setNewToolName] = useState('');
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  // Load reCAPTCHA script
  useEffect(() => {
    function renderRecaptcha() {
      if (window.grecaptcha && typeof window.grecaptcha.render === 'function' && recaptchaRef.current) {
        window.grecaptcha.render(recaptchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token) => setRecaptchaToken(token),
          'expired-callback': () => setRecaptchaToken(''),
        });
        return true;
      }
      return false;
    }

    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
      // Poll for grecaptcha until it's ready
      const interval = setInterval(() => {
        if (renderRecaptcha()) {
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    } else {
      renderRecaptcha();
    }
  }, []);
    // Fetch tools, objects, and categories
    useEffect(() => {
      fetch(`${getApiBaseUrl()}/api/tools`)
        .then((response) => response.json())
        .then((data) => {
          const sortedTools = data.sort((a, b) => a.name.localeCompare(b.name));
          setTools(sortedTools);
        })
        .catch((error) => console.error('Error fetching tools:', error));

      fetch(`${getApiBaseUrl()}/api/objects`)
        .then((response) => response.json())
        .then((data) => {
          const sortedObjects = data.sort((a, b) => a.name.localeCompare(b.name));
          setObjects(sortedObjects);
        })
        .catch((error) => console.error('Error fetching objects:', error));

      fetch(`${getApiBaseUrl()}/api/categories`)
        .then((response) => response.json())
        .then((data) => {
          const sortedCategories = data.sort((a, b) => a.name.localeCompare(b.name));
          setCategories(sortedCategories);
        })
        .catch((error) => console.error('Error fetching categories:', error));
    }, []);

    // Create object handler
    const handleCreateObject = async (inputValue) => {
      const trimmedValue = inputValue.trim();
      if (!trimmedValue) return;
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/objects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmedValue }),
        });

        if (!response.ok) {
          const data = await response.json();
          setError(data.error || 'Error creating object');
          return;
        }

        const newObject = await response.json();
        setObjects((prev) => [...prev, newObject]);
        setSelectedObjects((prev) => [...prev, { value: newObject.id, label: newObject.name }]);
        setError('');
        setSuccess('Objet créé !');
        setShowAddObject(false);
        setNewObjectName('');
      } catch (err) {
        console.error('Error creating object:', err);
        setError('Error creating object');
      }
    };

    // Create tool handler
    const handleCreateTool = async (inputValue) => {
      const trimmedValue = inputValue.trim();
      if (!trimmedValue) return;
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/tools`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmedValue }),
        });

        if (!response.ok) {
          const data = await response.json();
          setError(data.error || 'Error creating tool');
          return;
        }

        const newTool = await response.json();
        setTools((prev) => [...prev, newTool]);
        setSelectedTools((prev) => [...prev, { value: newTool.id, label: newTool.name }]);
        setError('');
        setSuccess('Outil créé !');
        setShowAddTool(false);
        setNewToolName('');
      } catch (err) {
        console.error('Error creating tool:', err);
        setError('Error creating tool');
      }
    };

  // Reasonable limits
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB (hard limit, server)
  const RESIZE_FILE_SIZE = 2 * 1024 * 1024; // 2MB (resize if above)
  const MAX_DIMENSION = 1920; // px

  // Helper to resize image if too large (with object URL cleanup)
  async function resizeImageIfNeeded(file) {
    if (!file || !(file instanceof File)) return file;
    if (file.size <= RESIZE_FILE_SIZE) return file;
    return new Promise((resolve) => {
      const img = new window.Image();
      const objectUrl = URL.createObjectURL(file);
      let settled = false;
      img.onload = function () {
        let { width, height } = img;
        let scale = 1;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          scale = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        } else if (file.size > RESIZE_FILE_SIZE) {
          // If file is still too big, scale down by file size ratio
          scale = Math.sqrt(RESIZE_FILE_SIZE / file.size);
        }
        if (scale >= 1) {
          if (!settled) {
            settled = true;
            URL.revokeObjectURL(objectUrl);
            resolve(file);
          }
          return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (!settled) {
            settled = true;
            URL.revokeObjectURL(objectUrl);
            if (!blob) return resolve(file);
            const ext = file.name.split('.').pop();
            const newFile = new File([blob], file.name.replace(/\.[^.]+$/, '') + '_resized.' + ext, { type: blob.type });
            resolve(newFile);
          }
        }, file.type, 0.85);
      };
      img.onerror = function () {
        if (!settled) {
          settled = true;
          URL.revokeObjectURL(objectUrl);
          resolve(file);
        }
      };
      img.src = objectUrl;
    });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Trim all user input fields before validation and submission
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const trimmedSelectedObjects = selectedObjects.map(obj => ({ ...obj, label: obj.label.trim() }));
    const trimmedSelectedTools = selectedTools.map(tool => ({ ...tool, label: tool.label.trim() }));
    const trimmedSelectedCategories = selectedCategories.map(cat => ({ ...cat, label: cat.label.trim() }));

    if (!trimmedName || !afterImage || trimmedSelectedObjects.length === 0 || !difficulty) {
      setError('Le nom, une photo du projet, au moins un objet sauvé et la difficulté sont requis');
      setPopupType('error');
      setShowPopup(true);
      return;
    }
    if (process.env.NODE_ENV === 'production' && !recaptchaToken) {
      setError('Veuillez valider le reCAPTCHA pour continuer.');
      setPopupType('error');
      setShowPopup(true);
      return;
    }

    // Resize images if needed
    let processedBeforeImage = beforeImage;
    let processedAfterImage = afterImage;
    if (beforeImage) processedBeforeImage = await resizeImageIfNeeded(beforeImage);
    if (afterImage) processedAfterImage = await resizeImageIfNeeded(afterImage);

    // Fallback: if resizing fails and file is still too large, show error
    if (processedBeforeImage && processedBeforeImage.size > MAX_FILE_SIZE) {
      setError('La photo "avant" est trop volumineuse et n’a pas pu être réduite automatiquement. Veuillez choisir une image plus petite (max 10 Mo).');
      setPopupType('error');
      setShowPopup(true);
      return;
    }
    if (processedAfterImage && processedAfterImage.size > MAX_FILE_SIZE) {
      setError('La photo du projet est trop volumineuse et n’a pas pu être réduite automatiquement. Veuillez choisir une image plus petite (max 10 Mo).');
      setPopupType('error');
      setShowPopup(true);
      return;
    }

    setLoading(true);

    // Helper to sanitize filenames: remove accents and special characters
    const sanitizeFilename = (filename) => {
      let sanitized = filename.normalize('NFD').replace(/\p{Diacritic}/gu, '');
      sanitized = sanitized.replace(/[^a-zA-Z0-9._-]/g, '_');
      return sanitized;
    };

    const formData = new FormData();
    formData.append('name', trimmedName);
    if (processedBeforeImage) {
      const sanitizedBefore = new File([processedBeforeImage], sanitizeFilename(processedBeforeImage.name), { type: processedBeforeImage.type });
      formData.append('beforeImage', sanitizedBefore);
    }
    if (processedAfterImage) {
      const sanitizedAfter = new File([processedAfterImage], sanitizeFilename(processedAfterImage.name), { type: processedAfterImage.type });
      formData.append('afterImage', sanitizedAfter);
    }
    formData.append('objects', JSON.stringify(trimmedSelectedObjects.map((obj) => obj.value)));
    formData.append('tools', JSON.stringify(trimmedSelectedTools.map((tool) => tool.value)));
    formData.append('description', trimmedDescription);
    formData.append('difficulty', difficulty);
    formData.append('recaptchaToken', recaptchaToken);
    if (trimmedSelectedCategories.length > 0) {
      formData.append('categories', JSON.stringify(trimmedSelectedCategories.map((cat) => cat.value)));
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/projects`, {
        method: 'POST',
        body: formData,
      });

      setLoading(false);
      if (response.ok) {
        setSuccess('Projet créé !');
        setPopupType('success');
        setShowPopup(true);
        setName('');
        setBeforeImage(null);
        setAfterImage(null);
        setSelectedTools([]);
        setSelectedObjects([]);
        setDescription('');
        setRecaptchaToken('');
        if (window.grecaptcha && recaptchaRef.current) {
          window.grecaptcha.reset();
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Erreur lors de la création du projet');
        setPopupType('error');
        setShowPopup(true);
      }
    } catch (err) {
      setLoading(false);
      setError('Erreur lors de la création du projet');
      setPopupType('error');
      setShowPopup(true);
    }
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Créer un nouveau projet</h1>
      {/* Loader overlay */}
      {loading && (
        <div style={loaderStyles.overlay}>
          <div style={loaderStyles.loader}>Chargement...</div>
        </div>
      )}
      {/* Popup for success or error */}
      {showPopup && (
        <div style={popupStyles.overlay}>
          <div style={popupStyles.popup}>
            <div style={popupStyles.message}>
              {popupType === 'success' ? (
                <>
                  <div style={{ fontWeight: 'bold', color: 'green', fontSize: 18, marginBottom: 10 }}>Projet créé !</div>
                  <div>Votre projet a bien été enregistré.</div>
                </>
              ) : (
                <>
                  <div style={{ fontWeight: 'bold', color: 'red', fontSize: 18, marginBottom: 10 }}>Erreur</div>
                  <div>{error || 'Erreur lors de la création du projet.'}</div>
                </>
              )}
            </div>
            <button
              style={{ ...styles.button, marginTop: 18, width: '100%' }}
              onClick={() => {
                setShowPopup(false);
                if (popupType === 'success') {
                  navigate('/');
                }
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formGroup}>
          <label htmlFor="name" style={styles.label}>Nom du projet (objet sauvé + usage) <span style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            id="name"
            placeholder="Ex: Raquettes de tennis porte manteau"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.input}
          />
        </div>
        <div style={styles.formGroup}>
          <label htmlFor="afterImage" style={styles.label}>Photo du projet <span style={{ color: 'red' }}>*</span></label>
          <input
            type="file"
            id="afterImage"
            accept="image/*"
            onChange={(e) => setAfterImage(e.target.files[0])}
            style={styles.input}
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Objets sauvés <span style={{ color: 'red' }}>*</span></label>
          <Select
            isMulti
            options={objects
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((object) => ({ value: object.id, label: object.name }))}
            value={selectedObjects}
            onChange={(selectedOptions) => setSelectedObjects(selectedOptions || [])}
            placeholder="Rechercher un objet existant"
            styles={customSelectStyles}
          />
          {!showAddObject && (
            <button type="button" style={{ ...styles.button, marginTop: 8, backgroundColor: '#007bff' }} onClick={() => setShowAddObject(true)}>
              Ajouter un nouvel objet
            </button>
          )}
          {showAddObject && (
            <div style={{ marginTop: 8 }}>
              <input
                type="text"
                value={newObjectName}
                onChange={e => setNewObjectName(e.target.value)}
                placeholder="Nom du nouvel objet"
                style={{ ...styles.input, marginBottom: 4 }}
              />
              <button
                type="button"
                style={{ ...styles.button, backgroundColor: '#28a745', marginRight: 8 }}
                onClick={() => newObjectName && handleCreateObject(newObjectName)}
              >
                Confirmer l'ajout
              </button>
              <button
                type="button"
                style={{ ...styles.button, backgroundColor: '#ccc', color: '#333' }}
                onClick={() => { setShowAddObject(false); setNewObjectName(''); }}
              >
                Annuler
              </button>
            </div>
          )}
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Niveau de difficulté <span style={{ color: 'red' }}>*</span></label>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '10px' }}>
            {[1, 2, 3].map((level) => {
              let iconSrc = '';
              let alt = '';
              if (level === 1) { iconSrc = '/assets/site/low-level.svg'; alt = 'Faible'; }
              if (level === 2) { iconSrc = '/assets/site/medium-level.svg'; alt = 'Moyenne'; }
              if (level === 3) { iconSrc = '/assets/site/high-level.svg'; alt = 'Élevée'; }
              return (
                <div
                  key={level}
                  style={{
                    cursor: 'pointer',
                    border: difficulty === level ? '2px solid #1ec31e' : '2px solid transparent',
                    borderRadius: '8px',
                    padding: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                  onClick={() => setDifficulty(level)}
                >
                  <img
                    src={iconSrc}
                    alt={alt}
                    style={{ width: '40px', height: '40px', filter: 'invert(52%) sepia(99%) saturate(749%) hue-rotate(74deg) brightness(92%) contrast(92%)', display: 'block' }}
                  />
                  <div style={{ textAlign: 'center', color: '#1ec31e', fontWeight: difficulty === level ? 'bold' : 'normal', fontSize: '14px', marginTop: '4px' }}>
                    {level === 1 ? 'Faible' : level === 2 ? 'Moyenne' : 'Élevée'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Catégorie(s)</label>
          <Select
            isMulti
            options={categories
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((cat) => ({ value: cat.id, label: cat.name }))}
            value={selectedCategories}
            onChange={(selectedOptions) => setSelectedCategories(selectedOptions || [])}
            placeholder="Rechercher et sélectionner des catégories"
            styles={customSelectStyles}
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Outils utilisés</label>
          <Select
            isMulti
            options={tools
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((tool) => ({ value: tool.id, label: tool.name }))}
            value={selectedTools}
            onChange={(selectedOptions) => setSelectedTools(selectedOptions || [])}
            placeholder="Rechercher un outil existant"
            styles={customSelectStyles}
          />
          {!showAddTool && (
            <button type="button" style={{ ...styles.button, marginTop: 8, backgroundColor: '#007bff' }} onClick={() => setShowAddTool(true)}>
              Ajouter un nouvel outil
            </button>
          )}
          {showAddTool && (
            <div style={{ marginTop: 8 }}>
              <input
                type="text"
                value={newToolName}
                onChange={e => setNewToolName(e.target.value)}
                placeholder="Nom du nouvel outil"
                style={{ ...styles.input, marginBottom: 4 }}
              />
              <button
                type="button"
                style={{ ...styles.button, backgroundColor: '#28a745', marginRight: 8 }}
                onClick={() => newToolName && handleCreateTool(newToolName)}
              >
                Confirmer l'ajout
              </button>
              <button
                type="button"
                style={{ ...styles.button, backgroundColor: '#ccc', color: '#333' }}
                onClick={() => { setShowAddTool(false); setNewToolName(''); }}
              >
                Annuler
              </button>
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label htmlFor="description" style={styles.label}>Etapes de réalisation (1 étape par ligne)</label>
          <textarea
            id="description"
            placeholder="Décrivez les étapes nécessaires à la réalisation du projet !"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={styles.textarea}
          />
        </div>
        {process.env.NODE_ENV === 'production' && (
          <div style={{ margin: '18px 0' }}>
            <div ref={recaptchaRef} style={{ display: 'inline-block' }}></div>
          </div>
        )}
        <button type="submit" style={styles.button}>Créer le projet</button>
        <Link to="/" style={styles.cancelLink}>Annuler</Link>
      </form>
    </div>
  );
};

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    width: '100%', // Ensure full width
    borderRadius: '5px',
    border: '1px solid #ccc',
    padding: '5px',
    boxSizing: 'border-box', // Ensure consistent box model
  }),
  container: (provided) => ({
    ...provided,
    width: '100%', // Ensure the container matches the width
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: '#e0f7fa',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#007bff',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#007bff',
    ':hover': {
      backgroundColor: '#007bff',
      color: 'white',
    },
  }),
};

const styles = {
  page: { textAlign: 'center', padding: '20px' },
  title: { fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' },
  form: { maxWidth: '400px', margin: '0 auto', textAlign: 'left' },
  formGroup: {
    marginBottom: '15px',
    width: '100%', // Ensure the form group takes the full width
  },
  label: { display: 'block', marginBottom: '5px', fontWeight: 'bold' },
  input: {
    width: '100%', // Full width
    padding: '10px',
    fontSize: '16px',
    borderRadius: '5px',
    border: '1px solid #ccc',
    boxSizing: 'border-box', // Ensure consistent box model
  },
  textarea: { width: '100%', padding: '10px', fontSize: '16px', borderRadius: '5px', border: '1px solid #ccc', height: '100px' },
  button: { padding: '10px 20px', fontSize: '16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' },
  cancelLink: { fontSize: '16px', color: '#007bff', textDecoration: 'none', cursor: 'pointer', marginLeft: '40px' },
  error: { color: 'red', marginBottom: '10px' },
  success: { color: 'green', marginBottom: '10px' },
};

export default CreateProjectPage;